<script lang="ts" setup>
import OmniDialog from "@/components/omni/OmniDialog.vue";
import { RouteStatus } from "@/constants/trip";
import { computed, ref } from "vue";
import { API } from "@/api/api";
import { useSnackbarStore } from "@/store/snackbar.store";
import type { Response } from "@/api/client";
import type { TruckRoute } from "@/api/trucks/dto/truck.dto";

const api = new API();
const { showSnackbarError, showSnackbarSuccess } = useSnackbarStore();

const model = defineModel<boolean>({ default: false });

const props = defineProps<{
  route: TruckRoute;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "updateRoutes"): void;
}>();

const loading = ref<boolean>(false);

const setStatusAs = computed(() => {
  return props.route.status === RouteStatus.planned ? "active" : "planned";
});

const title = computed(() => {
  return `Set route as ${setStatusAs.value}`;
});

async function changeStatus() {
  if (!props.route.id) {
    return;
  }

  try {
    loading.value = true;
    let response: Response<string> | undefined;

    if (props.route.status === RouteStatus.planned) {
      response = await api.events.setRouteAsCurrent(props.route.id);
    } else {
      response = await api.events.setRouteAsPlanned(props.route.id);
    }

    if (response.success) {
      showSnackbarSuccess("Route status changed successfully");
      emits("updateRoutes");
    } else if (response.errorData?.detail) {
      showSnackbarError(response.errorData.detail);
    }
  } finally {
    loading.value = false;
    emits("update:modelValue", false);
  }
}
</script>

<template>
  <omni-dialog
    v-model="model"
    :primary-button-disabled="loading"
    :title="title"
    primary-action-label="Apply"
    @primary-action:click="changeStatus"
  >
    <span class="text-grey-darken-1 font-weight-500">Are you sure want to make this route {{ setStatusAs }}?</span>
  </omni-dialog>
</template>
